import { EntityDefinition } from '@/components/common/CRUDTable/types';

export const rbacRolesEntity: EntityDefinition = {
  name: 'rbac-roles',
  displayName: 'RBAC Roles',
  displayNameSingular: 'RBAC Role',
  apiEndpoint: '/rbac/roles',
  routePath: '/rbac/roles',
  idField: 'id',
  nameField: 'role',
  fields: [
    {
      key: 'role',
      label: 'Role Name',
      type: 'text',
      required: true,
      validation: {
        minLength: 2,
        maxLength: 50
      }
    },
    {
      key: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      validation: {
        maxLength: 500
      }
    },
    {
      key: 'permissions',
      label: 'Permissions',
      type: 'multiselect',
      required: false,
      options: [
        // User Management
        { value: 'user.read', label: 'Read Users' },
        { value: 'user.create', label: 'Create Users' },
        { value: 'user.update', label: 'Update Users' },
        { value: 'user.delete', label: 'Delete Users' },
        { value: 'user.deactivate', label: 'Deactivate Users' },
        { value: 'user.reset_password', label: 'Reset User Passwords' },
        
        // Tenant Management
        { value: 'tenant.read', label: 'Read Tenants' },
        { value: 'tenant.create', label: 'Create Tenants' },
        { value: 'tenant.update', label: 'Update Tenants' },
        { value: 'tenant.delete', label: 'Delete Tenants' },
        { value: 'tenant.suspend', label: 'Suspend Tenants' },
        
        // Transformation Management
        { value: 'transformation.read', label: 'Read Transformations' },
        { value: 'transformation.create', label: 'Create Transformations' },
        { value: 'transformation.update', label: 'Update Transformations' },
        { value: 'transformation.delete', label: 'Delete Transformations' },
        { value: 'transformation.approve', label: 'Approve Transformations' },
        { value: 'transformation.execute', label: 'Execute Transformations' },
        
        // KPI Management
        { value: 'kpi.read', label: 'Read KPIs' },
        { value: 'kpi.create', label: 'Create KPIs' },
        { value: 'kpi.update', label: 'Update KPIs' },
        { value: 'kpi.delete', label: 'Delete KPIs' },
        { value: 'kpi.approve', label: 'Approve KPIs' },
        { value: 'kpi.export', label: 'Export KPIs' },
        
        // Process Management
        { value: 'process.read', label: 'Read Processes' },
        { value: 'process.create', label: 'Create Processes' },
        { value: 'process.update', label: 'Update Processes' },
        { value: 'process.delete', label: 'Delete Processes' },
        { value: 'process.execute', label: 'Execute Processes' },
        
        // Dashboard Management
        { value: 'dashboard.read', label: 'Read Dashboards' },
        { value: 'dashboard.create', label: 'Create Dashboards' },
        { value: 'dashboard.update', label: 'Update Dashboards' },
        { value: 'dashboard.delete', label: 'Delete Dashboards' },
        { value: 'dashboard.share', label: 'Share Dashboards' },
        
        // Report Management
        { value: 'report.read', label: 'Read Reports' },
        { value: 'report.create', label: 'Create Reports' },
        { value: 'report.update', label: 'Update Reports' },
        { value: 'report.delete', label: 'Delete Reports' },
        { value: 'report.export', label: 'Export Reports' },
        { value: 'report.schedule', label: 'Schedule Reports' },
        
        // System Management
        { value: 'system.administer', label: 'Administer System' },
        { value: 'system.configure', label: 'Configure System' },
        { value: 'system.backup', label: 'System Backup' },
        { value: 'system.restore', label: 'System Restore' },
        { value: 'system.logs', label: 'View System Logs' },
        
        // Navigation Permissions
        { value: 'nav.dashboard', label: 'Access Dashboard' },
        { value: 'nav.kpis', label: 'Access KPIs' },
        { value: 'nav.settings', label: 'Access Settings' },
        { value: 'nav.transformations', label: 'Access Transformations' },
        { value: 'nav.users', label: 'Access Users' },
        { value: 'nav.alignment', label: 'Access Strategic Alignment' },
        
        // Strategic Alignment Management
        { value: 'alignment.vision.read', label: 'Read Visions' },
        { value: 'alignment.vision.create', label: 'Create Visions' },
        { value: 'alignment.vision.update', label: 'Update Visions' },
        { value: 'alignment.vision.delete', label: 'Delete Visions' },
        { value: 'alignment.strategy.read', label: 'Read Strategies' },
        { value: 'alignment.strategy.create', label: 'Create Strategies' },
        { value: 'alignment.strategy.update', label: 'Update Strategies' },
        { value: 'alignment.strategy.delete', label: 'Delete Strategies' },
        { value: 'alignment.link.read', label: 'Read Alignment Links' },
        { value: 'alignment.link.create', label: 'Create Alignment Links' },
        { value: 'alignment.link.update', label: 'Update Alignment Links' },
        { value: 'alignment.link.delete', label: 'Delete Alignment Links' },
        { value: 'alignment.report.read', label: 'Read Alignment Reports' },
        
        // ABAC Management
        { value: 'abac_attribute.read', label: 'Read ABAC Attributes' },
        { value: 'abac_attribute.create', label: 'Create ABAC Attributes' },
        { value: 'abac_attribute.update', label: 'Update ABAC Attributes' },
        { value: 'abac_attribute.delete', label: 'Delete ABAC Attributes' },
        { value: 'abac_policy.read', label: 'Read ABAC Policies' },
        { value: 'abac_policy.create', label: 'Create ABAC Policies' },
        { value: 'abac_policy.update', label: 'Update ABAC Policies' },
        { value: 'abac_policy.delete', label: 'Delete ABAC Policies' },
        
        // General Actions
        { value: 'general.read', label: 'General Read Access' },
        { value: 'general.create', label: 'General Create Access' },
        { value: 'general.update', label: 'General Update Access' },
        { value: 'general.delete', label: 'General Delete Access' },
        { value: 'general.approve', label: 'General Approve Access' },
        { value: 'general.reject', label: 'General Reject Access' },
        { value: 'general.assign', label: 'General Assign Access' },
        { value: 'general.revoke', label: 'General Revoke Access' },
        { value: 'general.export', label: 'General Export Access' },
        { value: 'general.import', label: 'General Import Access' },
        { value: 'general.execute', label: 'General Execute Access' }
      ]
    },
    {
      key: 'isActive',
      label: 'Active',
      type: 'boolean',
      required: true,
      defaultValue: true
    },
    {
      key: 'canBeModified',
      label: 'Can Be Modified',
      type: 'boolean',
      required: true,
      defaultValue: true,
      readOnly: true
    }
  ],
  tableColumns: [
    {
      key: 'role',
      label: 'Role Name',
      sortable: true
    },
    {
      key: 'description',
      label: 'Description',
      sortable: false
    },
    {
      key: 'isActive',
      label: 'Status',
      sortable: true,
      render: (value: unknown) => {
        const isActive = value as boolean;
        return isActive ? 'Active' : 'Inactive';
      }
    }
  ],
  searchFields: ['role', 'description'],
  defaultSort: {
    field: 'role',
    direction: 'asc'
  },
  permissions: {
    create: true,
    read: true,
    update: true,
    delete: true
  }
};
